import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { shareImageFromUrl } from "helpers/shareImage";

// Share state for the LightBox share button: preparing = downloading/converting the picture
// (can take a few seconds), ignored re-taps while it runs, cancel() aborts it.
export const useShareImage = () => {
  const [preparing, setPreparing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const share = async (imageUrl: string) => {
    if (abortRef.current) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setPreparing(true);
    try {
      await shareImageFromUrl(imageUrl, undefined, () => setPreparing(false), controller.signal);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setPreparing(false);
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPreparing(false);
  };

  useEffect(() => () => abortRef.current?.abort(), []); // lightbox unmounted mid-prepare

  return { preparing, share, cancel };
};

// Modal dialog over the lightbox (lightboxes use z-index ~1000-9999).
// Portal to <body>: the lightboxes portal themselves to <body>, so rendering inline (inside the chat
// page's stacking context) left this dialog hidden behind them whatever its z-index.
const SharePreparingOverlay = ({ show, onCancel }: { show: boolean; onCancel: () => void }) => {
  if (!show) return null;
  return createPortal((
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 20000, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        role="dialog" aria-modal="true"
        style={{
          background: "#fff", color: "#212529", borderRadius: 12, padding: "24px 24px 16px",
          width: 280, maxWidth: "100%", textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        }}
      >
        <i className="bx bx-loader-alt bx-spin" style={{ fontSize: 36, color: "#0d6efd" }}></i>
        <div style={{ marginTop: 12, fontSize: 16 }}>กำลังเตรียมรูปภาพ...</div>
        <button type="button" className="btn btn-light" style={{ marginTop: 16, minWidth: 100 }} onClick={onCancel}>
          ยกเลิก
        </button>
      </div>
    </div>
  ), document.body);
};

export default SharePreparingOverlay;
